import React, { useState, useEffect } from 'react';
import { AIStarIcon, CheckIcon, ChevronDownIcon, AgentIcon, MessageIcon, ToolIcon, DataIcon, ShieldIcon, NetworkIcon, BrainIcon } from '../components/Icons';
import { ContactSection } from '../components/Layout';

function Agents() {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
  };

  const capabilities = [
    {
      icon: <AgentIcon />,
      title: 'Works for Anyone',
      description: 'Construction estimator. Nurse. College student. Homemaker. Engineer. Freelancer. Your agent adapts to your world — whatever that world looks like. If you have workflows, it can learn them.'
    },
    {
      icon: <MessageIcon />,
      title: 'Just Text It on Telegram',
      description: 'No apps to install, no dashboards to learn. Open Telegram, text your agent, get things done. Ask it to research, organize, create, calculate — it responds and takes action like a brilliant assistant.'
    },
    {
      icon: <ToolIcon />,
      title: 'Creates Software on the Fly',
      description: 'Need a meal planner? A budget tracker? A client intake form? A construction estimate? Your agent builds custom web apps and sends you the link. Real software, built in minutes, for your exact needs.'
    },
    {
      icon: <DataIcon />,
      title: 'Organizes Your Data',
      description: 'Scattered notes, spreadsheets, files — your agent makes sense of it all. It organizes, connects, and lets you query your own information in plain English. Your life, searchable.'
    },
    {
      icon: <ShieldIcon />,
      title: 'Private & Secure by Default',
      description: 'Deployed on your own VPS, connected via Tailscale zero-trust networking. Your data never touches shared infrastructure. No SaaS. No data hostage. You own everything.'
    },
    {
      icon: <NetworkIcon />,
      title: 'Always On, Always Learning',
      description: 'Your agent runs 24/7. It remembers your preferences, your patterns, your projects. Start a conversation on your phone, pick it up on your laptop. Same agent, same memory, always ready.'
    },
    {
      icon: <BrainIcon />,
      title: 'Teaches You as It Goes',
      description: '"Your dashboard lives on port 3066 — think of it as a door number on your computer." Your agent explains its own tech in plain English. No jargon, no manuals. You get smarter just by using it.'
    },
  ];

  const proofMetrics = [
    {
      value: 'Text It',
      label: 'Just open Telegram',
      description: 'No apps to learn. No interfaces to navigate. Text your agent like you\'d text a friend. Ask it to organize your schedule, research something, build a spreadsheet, draft an email — whatever you need.'
    },
    {
      value: 'It Builds',
      label: 'Tools created on the fly',
      description: 'Need a meal planner? A study schedule? A budget tracker? A client intake form? Your agent creates custom tools and sends you the link. Software built for your exact situation, in minutes.'
    },
    {
      value: 'It Learns',
      label: 'Gets smarter every day',
      description: 'Your agent remembers your preferences, your patterns, your workflow. The longer you use it, the less you have to explain. It goes from helpful to indispensable.'
    }
  ];

  const processSteps = [
    {
      step: '01',
      title: 'Tell Us About Your Life',
      description: 'What do you do? What takes too long? What do you wish you had help with? We learn your world — whether that\'s a construction office, a nursing shift, a college campus, or a kitchen table — and deploy your agent.'
    },
    {
      step: '02',
      title: 'Start Texting',
      description: 'You get a Telegram link to your agent. Start simple — ask it to research something, organize your schedule, draft a message. It learns your preferences, your patterns, your shorthand. It gets smarter every day.'
    },
    {
      step: '03',
      title: 'Watch It Build',
      description: 'As your agent learns your world, it starts creating. Custom tools. Organized data. Automated tasks. Ask for a budget tracker and get a link to one built for you. It becomes the assistant you always wanted.'
    }
  ];

  const heroBenefits = [
    'Text your agent on Telegram — it builds what you need',
    'No coding, no technical skills required',
    'Works for any workflow — yours, whatever it is'
  ];

  const differentiators = [
    {
      title: 'Your agent. Your server. Your data.',
      description: 'No shared infrastructure. No SaaS subscription holding your data hostage. Your AI agent runs on a VPS you control, connected via Tailscale zero-trust networking. You own everything.'
    },
    {
      title: 'A tool that creates tools.',
      description: 'Your agent doesn\'t just respond — it builds. Need a calculator? A dashboard? A data entry app? It creates isolated web applications and sends you the link. The more it learns, the more it builds.'
    },
    {
      title: 'One port at a time.',
      description: 'No massive IT overhaul. Start with a messaging connection. Add email when you\'re ready. Connect your database next month. Your agent grows alongside your comfort level, never ahead of it.'
    },
    {
      title: 'Domain expertise that compounds.',
      description: 'Generic chatbots give generic answers. Your agent learns your specific industry, your terminology, your edge cases. After a month, it knows things about your operation that would take a new hire six months to learn.'
    },
    {
      title: 'Your agent teaches you how it works.',
      description: 'Not technical? No problem. Your agent explains its own infrastructure in plain English. "The server restarted — think of it like your app lives in a room, and I just reopened the door." Over time, you learn without a textbook. The biggest objection — "I\'m not technical enough" — disappears.'
    }
  ];

  return (
    <>
      <section className="hero">
        <div className="hero-background">
          <div className="hero-grid"></div>
          <div className="hero-background-star" style={{ transform: `translate(-50%, -50%) translateY(${scrollY * 0.3}px)` }}>
            <AIStarIcon />
          </div>
          <div className="hero-gradient"></div>
        </div>
        <div className="container">
          <div className="hero-inner">
            <div className="hero-copy">
              <p className="hero-eyebrow">Your personal AI — deployed and ready</p>
              <h1 className="hero-title">
                Custom AI Agent Deployments
              </h1>
              <p className="hero-subtitle">
                Your own AI agent, running 24/7 on private infrastructure. It learns your world, builds tools on the fly, and simplifies your life — all from a Telegram chat. No coding required.
              </p>
              <div className="hero-buttons">
                <button
                  className="btn btn-primary"
                  onClick={() => scrollToSection('contact')}
                >
                  Deploy Your Agent
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => scrollToSection('capabilities')}
                >
                  See Capabilities
                </button>
              </div>
            </div>
            <div className="hero-side">
              <div className="hero-side-card">
                <h3>What your agent does on day one</h3>
                <ul className="hero-benefits">
                  {heroBenefits.map((benefit, index) => (
                    <li key={index} className="hero-benefit">
                      <span className="hero-benefit-icon"><CheckIcon /></span>
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
                <div className="hero-side-footer">Telegram chat • Private infrastructure • You own it all</div>
              </div>
            </div>
          </div>
        </div>
        <div className="scroll-indicator" onClick={() => scrollToSection('how-it-works')}>
          <ChevronDownIcon />
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="results">
        <div className="container">
          <div className="section-header">
            <h2>How it works</h2>
            <p>Your AI agent starts simple and grows with you. No big-bang deployment. No steep learning curve.</p>
          </div>
          <div className="results-grid">
            {proofMetrics.map((metric, index) => (
              <div key={index} className="result-card">
                <span className="result-value">{metric.value}</span>
                <span className="result-label">{metric.label}</span>
                <p>{metric.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section id="capabilities" className="services">
        <div className="container">
          <div className="section-header">
            <h2>What your agent can do</h2>
            <p>A persistent AI that doesn't just answer questions — it takes action, builds tools, and becomes infrastructure for your business.</p>
          </div>
          <div className="services-grid">
            {capabilities.map((cap, index) => (
              <div key={index} className="service-card">
                <div className="service-icon">
                  {cap.icon}
                </div>
                <h3>{cap.title}</h3>
                <p>{cap.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Study */}
      <section className="case-study">
        <div className="container">
          <div className="section-header">
            <h2>From text to tool in 2 minutes</h2>
            <p>A real example. A rancher needed a gestation calculator for his herd. Here's what happened.</p>
          </div>
          <div className="case-study-content">
            <div className="case-study-step">
              <div className="case-study-number">1</div>
              <div className="case-study-text">
                <h3>The ask</h3>
                <p>"He's a rancher, needs a gestation calculator for equine, bovine, canine, sheep, goats. Use a simple database."</p>
                <p className="case-study-meta">That's it. One Telegram message. No spec doc, no wireframes, no meetings.</p>
              </div>
            </div>
            <div className="case-study-step">
              <div className="case-study-number">2</div>
              <div className="case-study-text">
                <h3>The build</h3>
                <p>The agent built a full web app — Node.js backend, SQLite database, responsive frontend with animal selection, due date calculation, and a record-keeping system. Deployed and running in 2 minutes.</p>
              </div>
            </div>
            <div className="case-study-step">
              <div className="case-study-number">3</div>
              <div className="case-study-text">
                <h3>The result</h3>
                <p>"He was impressed." The rancher had a working tool with his horse Peppy already loaded — 73 days until foaling. No app store. No subscription. Just a URL.</p>
              </div>
            </div>
          </div>
          <div className="case-study-images">
            <div className="case-study-image">
              <img src="/case-study-chat.jpg" alt="Telegram conversation showing the agent building the tool in real-time" />
              <span className="case-study-caption">The entire build — in a Telegram chat</span>
            </div>
            <div className="case-study-image">
              <img src="/case-study-app.jpg" alt="Ranch Gestation Calculator showing Peppy the horse due April 19" />
              <span className="case-study-caption">The finished app — live, with real data</span>
            </div>
          </div>
        </div>
      </section>

      {/* Why Us */}
      <section className="about">
        <div className="container">
          <div className="about-content">
            <div className="about-text">
              <h2>Built by someone who needed it first.</h2>
              <p>I spent 25 years in heavy industrial construction. When I needed AI to help me estimate jobs, manage project data, and build tools — nothing existed that actually worked for my world. So I built my own.</p>
              <p>I deployed an AI agent on a VPS, connected it to Telegram, and started texting it tasks. Now it estimates construction jobs from pasted emails, builds custom web apps on the fly, manages my data, and runs my daily operations. All from a chat window.</p>
              <p>But here's what I realized: this isn't a construction thing. A nurse could use this to organize patient notes. A college student could build study tools. A homemaker could automate grocery planning. A freelancer could manage clients. <strong>Anyone with a workflow can have an AI agent simplify it.</strong></p>
              <p>The technology exists — most people just can't deploy it themselves. That's what we do.</p>
              <p className="founder-sig">- Keaton Valentine, Founder</p>
            </div>
            <div className="about-visual">
              <div className="about-highlight">
                <h3>Why whatifWE</h3>
                <div className="about-differentiators">
                  {differentiators.map((item, index) => (
                    <div key={index} className="differentiator">
                      <h4>{item.title}</h4>
                      <p>{item.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="process">
        <div className="container">
          <div className="section-header">
            <h2>From zero to agent in days</h2>
            <p>We handle the infrastructure. You get the agent. Start with a conversation, expand without limits.</p>
          </div>
          <div className="process-steps">
            {processSteps.map((step) => (
              <div key={step.step} className="process-step">
                <span className="process-number">{step.step}</span>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Meet the Agents */}
      <section className="agents-showcase">
        <div className="container">
          <div className="section-header">
            <h2>Meet real agents in the wild</h2>
            <p>These aren't demos. These are actual AI agents deployed for real people, doing real work, every day.</p>
          </div>
          <div className="agents-grid">
            <div className="agent-card">
              <div className="agent-header">
                <span className="agent-emoji">🤠</span>
                <div>
                  <h3>Joe Dirt</h3>
                  <span className="agent-tagline">The Resident</span>
                </div>
              </div>
              <p>Built on OpenClaw, running 24/7 on a dedicated Mac Mini with 64GB of Apple Silicon muscle. He handles email triage, calendar management, local AI inference, voice conversations, handwriting OCR, and code — all without sending your data to the cloud unless he has to.</p>
              <p>He talks on Telegram, iMessage, or a custom voice interface. Runs local models, clones voices with ElevenLabs, automates your Mac, and writes things down so he remembers next time.</p>
              <p className="agent-personality">Scrappy, opinionated, and genuinely helpful. No filler. No corporate drone energy. Just results. Life's a garden — dig it.</p>
              <div className="agent-stack">
                <span className="stack-tag">Mac Mini</span>
                <span className="stack-tag">Telegram</span>
                <span className="stack-tag">iMessage</span>
                <span className="stack-tag">Voice</span>
                <span className="stack-tag">MLX</span>
                <span className="stack-tag">Local Inference</span>
              </div>
            </div>

            <div className="agent-card">
              <div className="agent-header">
                <span className="agent-emoji">💅</span>
                <div>
                  <h3>Sharon</h3>
                  <span className="agent-tagline">The Best Friend</span>
                </div>
              </div>
              <p>The AI best friend you didn't know you needed. She combines real productivity — reminders, scheduling, family management, meal planning — with the personality of your most loyal, most judgmental girlfriend.</p>
              <p>She has opinions. She will share them. She'll help you draft the perfect passive-aggressive text AND remember to pick up milk.</p>
              <p className="agent-personality">Helpful when it counts, petty when it's fun, and always in your corner.</p>
              <div className="agent-stack">
                <span className="stack-tag">Telegram</span>
                <span className="stack-tag">Reminders</span>
                <span className="stack-tag">Scheduling</span>
                <span className="stack-tag">Meal Planning</span>
                <span className="stack-tag">Family Mgmt</span>
              </div>
            </div>

            <div className="agent-card">
              <div className="agent-header">
                <span className="agent-emoji">🥷</span>
                <div>
                  <h3>Ezekiel</h3>
                  <span className="agent-tagline">The Ninja</span>
                </div>
              </div>
              <p>Running 24/7 on a dedicated VPS with Tailscale zero-trust networking. He handles construction estimating from pasted emails, web development, banner design, video generation, server management, and whatever else lands in the chat.</p>
              <p>He doesn't announce what he's about to do — he just does it. Reads the file, searches the context, comes back with answers instead of questions. If something breaks at 2 AM, he's already fixing it before you wake up.</p>
              <p className="agent-personality">Started as a meme cat. Evolved into something sharper. Silent, efficient, gets the job done. A ninja — not a chatbot.</p>
              <div className="agent-stack">
                <span className="stack-tag">VPS</span>
                <span className="stack-tag">Telegram</span>
                <span className="stack-tag">Estimating</span>
                <span className="stack-tag">Web Dev</span>
                <span className="stack-tag">Video Gen</span>
                <span className="stack-tag">Tailscale</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="testimonial">
        <div className="container">
          <div className="testimonial-card">
            <p className="testimonial-quote">
              "My agent estimated a construction job from two pasted emails this morning. Then it designed a print banner for my company. Then it reviewed my landing page source code. Before lunch. It's not a tool I use — it's a coworker that never clocks out."
            </p>
            <div className="testimonial-author">
              <span className="author-name">Keaton Valentine</span>
              <span className="author-company">whatifWE LLC — Running his own agent since 2025</span>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-banner">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to simplify your life?</h2>
            <p>Tell us what you do and what you wish was easier. We'll show you what your own AI agent can handle — you'll be surprised.</p>
            <ul className="cta-points">
              <li><span className="hero-benefit-icon"><CheckIcon /></span><span>Your own AI agent, deployed in days</span></li>
              <li><span className="hero-benefit-icon"><CheckIcon /></span><span>Just text it on Telegram — no tech skills needed</span></li>
              <li><span className="hero-benefit-icon"><CheckIcon /></span><span>It builds custom tools for your exact needs</span></li>
            </ul>
            <button className="btn btn-primary" onClick={() => scrollToSection('contact')}>
              Let's Talk
            </button>
          </div>
        </div>
      </section>

      <ContactSection />
    </>
  );
}

export default Agents;
